import { api } from "../api";
import type { AxiosResponse } from "axios";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EquipmentSpecs {
  // ── Módulo fotovoltaico ───────────────────────────────────────────
  nominalPowerWp?: number;        // Potência de pico (Wp)
  isc?: number;                   // Corrente de curto-circuito (A)
  voc?: number;                   // Tensão de circuito aberto (V)
  vmp?: number;                   // Tensão de máxima potência (V)
  imp?: number;                   // Corrente de máxima potência (A)
  moduleArea?: number;            // Área do módulo (m²)
  efficiency?: number;            // Eficiência do módulo (%)
  tempCoefficientPmax?: number;   // Coef. temperatura Pmáx (%/°C)
  tempCoefficientVoc?: number;    // Coef. temperatura Voc (%/°C)
  tempCoefficientIsc?: number;    // Coef. temperatura Isc (%/°C)
  maxSystemVoltage?: number;      // Tensão máxima do sistema (V)
  bypassDiodes?: number;          // Número de diodos bypass
  cells?: number;                 // Número de células
  cellType?: string;              // Tipo de célula (monocristalino, etc.)

  // ── Inversor ─────────────────────────────────────────────────────
  nominalPowerW?: number;         // Potência nominal de saída CA (W)
  nominalPowerKW?: number;        // Potência nominal de saída CA (kW)
  maxInputPowerW?: number;        // Máxima potência CC de entrada (W)
  maxDcVoltageV?: number;         // Máxima tensão CC (V)
  nominalDcVoltageV?: number;     // Tensão nominal CC (V)
  inputVoltageRangeV?: string;    // Faixa de tensão MPPT (ex: "200-1000V")
  mpptCount?: number;             // Número de MPPTs independentes
  stringsPerMppt?: number;        // Strings por MPPT
  maxInputCurrentA?: number;      // Máxima corrente de entrada por MPPT (A)
  maxOutputCurrentA?: number;     // Máxima corrente de saída CA (A)
  acPhases?: 1 | 3;                // Tipo de saída CA do inversor (mono ou tri)
  nominalOutputVoltageV?: string; // Tensão nominal CA (ex: "220/380V")
  nominalFrequencyHz?: string;    // Frequência de rede CA (ex: "50/60Hz")
  maxEfficiencyPct?: number;      // Máxima eficiência (%)
  euroEfficiencyPct?: number;     // Eficiência europeia (%)
  mpptEfficiencyPct?: number;     // Eficiência MPPT (%)
  powerFactor?: string;           // Fator de potência
  thdi?: string;                  // THDi
  ipRating?: string;              // Grau de proteção IP

  // ── Bateria / armazenamento ──────────────────────────────────────
  chemistry?: string;                    // Química (ex.: LiFePO4/LFP)
  nominalEnergyKWh?: number;             // Energia nominal (kWh)
  usableEnergyKWh?: number;              // Energia utilizável (kWh)
  nominalCapacityAh?: number;            // Capacidade nominal (Ah)
  nominalVoltageV?: number;              // Tensão nominal CC (V)
  operatingVoltageRangeV?: string;       // Faixa de tensão operacional
  maxChargeCurrentA?: number;            // Corrente máxima de carga (A)
  maxDischargeCurrentA?: number;         // Corrente máxima de descarga (A)
  maxChargePowerKW?: number;             // Potência contínua máxima de carga (kW)
  maxDischargePowerKW?: number;          // Potência contínua máxima de descarga (kW)
  peakDischargePowerKW?: number;         // Potência de pico de descarga (kW)
  depthOfDischargePct?: number;          // Profundidade de descarga (%)
  roundTripEfficiencyPct?: number;       // Eficiência de ciclo (%)
  cycleLife?: number;                    // Vida útil declarada (ciclos)
  maxParallelUnits?: number;             // Máximo de unidades em paralelo
  communication?: string;                // CAN, RS485 etc.
  operatingTemperatureRangeC?: string;   // Faixa de temperatura operacional
  warrantyYears?: number;                // Garantia (anos)

  // ── MPPTs assimétricos (quando cada tracker tem range/strings diferentes) ──
  // Se todos os MPPTs forem iguais, use mpptCount + stringsPerMppt + inputVoltageRangeV.
  // Se houver diferenças por tracker, use mpptChannels[] no lugar.
  mpptChannels?: Array<{
    index: number;          // número do MPPT (1-based)
    strings: number;        // entradas/strings neste tracker
    voltageRangeV?: string; // faixa de tensão (ex: "200-1000V")
    maxCurrentA?: number;   // corrente máxima deste tracker (A)
  }>;

  // ── Comuns ───────────────────────────────────────────────────────
  inmetro?: string;               // Número de registro Inmetro
  weightKg?: number;              // Peso (kg)
  dimensionsMm?: string;          // Dimensões (ex: "1762x1134x35")

  [key: string]: unknown;         // Escape hatch — use somente se o campo não existir acima
}

export interface Equipment {
  id: number;
  name: string;
  brand: string;
  model: string | null;
  category: string;
  inmetroNumber?: string | null;
  specs: EquipmentSpecs | null;
  datasheets?: Datasheet[];
  dataSheets?: Datasheet[];
  createdAt: string;
}

export interface Datasheet {
  id: number;
  equipmentId: number;
  fileName: string;
  originalName: string;
  documentType?: "DATASHEET" | "INMETRO_CERT";
  aiStatus: string;
  aiAnalysis: unknown;
  createdAt: string;
}

export interface ProjectEquipment {
  id: number;
  projectId: number;
  equipmentId: number;
  quantity: number;
  role: string;
  installationStatus: "NEW" | "EXISTING";
  equipment: Equipment;
}

export interface TechnicalData {
  projectId: number;
  connectionVoltage: string | null;
  connectionType: string | null;
  installedPowerAC: number | null;
  generatorPowerDC: number | null;
  numberOfModules: number | null;
  consumerClass: string | null;
  averageConsumption: number | null;
  installedLoad: number | null;
  installationNumber: string | null;
  meterNumber: string | null;
  utilityBreakerAmps: number | null;
  solarBreakerAmps: number | null;
  solarCableGaugeMm2: number | null;
  roofType: string | null;
  latitude: number | null;
  longitude: number | null;
  cableLengthCC: number | null;
  cableLengthCA: number | null;
  observations: string | null;
  locationImageBase64: string | null;
  meterBoxImageBase64: string | null;
  utilitySpecificData: {
    utility: string;
    schemaVersion: number;
    values: Record<string, unknown>;
  } | null;
}

export interface EquipmentFilters {
  search?: string;
  category?: string;
}

// ─── Dados Técnicos do Projeto ────────────────────────────────────────────────

export const getTechnicalData = async (projectId: number): Promise<TechnicalData> => {
  const response: AxiosResponse<TechnicalData> = await api.get(
    `/projects/${projectId}/technical-data`
  );
  return response.data;
};

export const saveTechnicalData = async (
  projectId: number,
  data: Partial<TechnicalData>
): Promise<TechnicalData> => {
  const response: AxiosResponse<TechnicalData> = await api.post(
    `/projects/${projectId}/technical-data`,
    data
  );
  return response.data;
};

// ─── Catálogo Global de Equipamentos ─────────────────────────────────────────

export const listEquipment = async (filters: EquipmentFilters = {}): Promise<Equipment[]> => {
  const response: AxiosResponse<Equipment[]> = await api.get("/equipment", { params: filters });
  return response.data;
};

export const getEquipmentById = async (id: number): Promise<Equipment> => {
  const response: AxiosResponse<Equipment> = await api.get(`/equipment/${id}`);
  return response.data;
};

export const createEquipment = async (
  data: Omit<Equipment, "id" | "createdAt">
): Promise<Equipment> => {
  const response: AxiosResponse<Equipment> = await api.post("/equipment", data);
  return response.data;
};

export const updateEquipment = async (
  id: number,
  data: Partial<Omit<Equipment, "id" | "createdAt">>
): Promise<Equipment> => {
  const response: AxiosResponse<Equipment> = await api.put(`/equipment/${id}`, data);
  return response.data;
};

export type DeleteEquipmentResult = Equipment & {
  action?: "DELETED" | "DEACTIVATED";
  message?: string;
};

export const deleteEquipment = async (id: number): Promise<DeleteEquipmentResult> => {
  const response: AxiosResponse<DeleteEquipmentResult> = await api.delete(`/equipment/${id}`);
  return response.data;
};

// ─── Datasheets ───────────────────────────────────────────────────────────────

export const uploadDatasheet = async (
  equipmentId: number,
  file: File,
  documentType: "DATASHEET" | "INMETRO_CERT" = "DATASHEET"
): Promise<Datasheet> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("documentType", documentType);
  const response: AxiosResponse<Datasheet> = await api.post(
    `/equipment/${equipmentId}/datasheet`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

export const listDatasheets = async (equipmentId: number): Promise<Datasheet[]> => {
  const response: AxiosResponse<Datasheet[]> = await api.get(
    `/equipment/${equipmentId}/datasheets`
  );
  return response.data;
};

// ─── Equipamentos do Projeto ──────────────────────────────────────────────────

export const getProjectEquipment = async (
  projectId: number
): Promise<ProjectEquipment[]> => {
  const response: AxiosResponse<ProjectEquipment[]> = await api.get(
    `/projects/${projectId}/equipment`
  );
  return response.data;
};

export const addProjectEquipment = async (
  projectId: number,
  equipmentId: number,
  quantity: number,
  role: string,
  installationStatus: "NEW" | "EXISTING" = "NEW"
): Promise<ProjectEquipment> => {
  const response: AxiosResponse<ProjectEquipment> = await api.post(
    `/projects/${projectId}/equipment`,
    { equipmentId, quantity, role, installationStatus }
  );
  return response.data;
};

export const updateProjectEquipment = async (
  projectId: number,
  projectEquipmentId: number,
  quantity: number,
  installationStatus?: "NEW" | "EXISTING"
): Promise<ProjectEquipment> => {
  const response: AxiosResponse<ProjectEquipment> = await api.put(
    `/projects/${projectId}/equipment/${projectEquipmentId}`,
    { quantity, installationStatus }
  );
  return response.data;
};

export const removeProjectEquipment = async (
  projectId: number,
  equipmentId: number,
  role?: string,
  installationStatus?: "NEW" | "EXISTING"
): Promise<void> => {
  const params = { ...(role ? { role } : {}), ...(installationStatus ? { installationStatus } : {}) };
  await api.delete(`/projects/${projectId}/equipment/${equipmentId}`, { params });
};

// ─── Datasheet AI ─────────────────────────────────────────────────────────────

export const getDatasheet = async (datasheetId: number): Promise<Datasheet> => {
  const response: AxiosResponse<Datasheet> = await api.get(
    `/equipment/datasheets/${datasheetId}`
  );
  return response.data;
};

export const reanalyzeDatasheet = async (datasheetId: number): Promise<Datasheet> => {
  const response: AxiosResponse<Datasheet> = await api.post(
    `/equipment/datasheets/${datasheetId}/analyze`
  );
  return response.data;
};

export const applyDatasheetAnalysis = async (
  datasheetId: number,
  modelIndex: number
): Promise<Equipment> => {
  const response: AxiosResponse<Equipment> = await api.post(
    `/equipment/datasheets/${datasheetId}/apply`,
    { modelIndex }
  );
  return response.data;
};

export const createAllModelsFromDatasheet = async (datasheetId: number): Promise<Equipment[]> => {
  const response: AxiosResponse<Equipment[]> = await api.post(
    `/equipment/datasheets/${datasheetId}/apply`,
    { createAll: true }
  );
  return response.data;
};

export const getDatasheetDownloadUrl = (datasheetId: number, inline = false): string => {
  const baseURL =
    process.env.NODE_ENV === "development"
      ? `http://${window.location.hostname}:8080`
      : (process.env.REACT_APP_BASE_URL ?? "").replace(/\/api\/?$/, "");
  return `${baseURL}/api/equipment/datasheets/${datasheetId}/download${inline ? "?inline=true" : ""}`;
};

export const getDatasheetInlineBlob = async (datasheetId: number): Promise<Blob> => {
  const response: AxiosResponse<Blob> = await api.get(
    `/equipment/datasheets/${datasheetId}/download`,
    { params: { inline: "true" }, responseType: "blob" }
  );
  return response.data;
};

export const downloadDatasheetFile = async (datasheetId: number, fileName: string): Promise<void> => {
  const response: AxiosResponse<Blob> = await api.get(
    `/equipment/datasheets/${datasheetId}/download`,
    { responseType: "blob" }
  );
  const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName || `datasheet-${datasheetId}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
};

export const deleteDatasheet = async (datasheetId: number): Promise<void> => {
  await api.delete(`/equipment/datasheets/${datasheetId}`);
};

// ─── Memorial Descritivo ──────────────────────────────────────────────────────

export const generateMemorial = async (
  projectId: number,
  locationImageBase64?: string | null,
  meterBoxImageBase64?: string | null,
): Promise<Blob> => {
  const response: AxiosResponse<Blob> = await api.post(
    `/solar/projects/${projectId}/memorial`,
    {
      locationImageBase64: locationImageBase64 ?? undefined,
      meterBoxImageBase64: meterBoxImageBase64 ?? undefined,
    },
    { responseType: "blob" }
  );
  return response.data;
};

// ─── CAD (Diagrama Unifilar DXF) ──────────────────────────────────────────────

export const generateUnifilarCad = async (projectId: number): Promise<Blob> => {
  const response: AxiosResponse<Blob> = await api.post(
    `/solar/projects/${projectId}/cad/unifilar`,
    {},
    { responseType: "blob" }
  );
  return response.data;
};

export interface CadSheetsArchive {
  blob: Blob;
  fileName: string;
  sheetCount: number;
}

export const downloadSeparatedCadSheets = async (
  projectId: number,
): Promise<CadSheetsArchive> => {
  const response = await api.get(
    `/solar/projects/${projectId}/cad/sheets`,
    { responseType: "blob" },
  );
  const disposition = String(response.headers["content-disposition"] ?? "");
  const fileNameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return {
    blob: response.data,
    fileName: fileNameMatch?.[1] ?? `Projeto-Eletrico-${projectId}-Pranchas-Separadas.zip`,
    sheetCount: Number(response.headers["x-cad-sheet-count"] ?? 0),
  };
};

export const saveEquatorialPiSituation = async (
  projectId: number,
  data: Record<string, unknown>,
): Promise<void> => {
  await api.put(`/solar/projects/${projectId}/cad/situation`, data);
};
